import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import styles from './ViewMoreModal.module.css';
import modalStyles from '../pages/lordGames/components/RightSection/RightSection.module.css';
import { API_BASE_URL } from '@/config/api';
import { isOwnerRole } from '@/utils/roles';

const ViewMoreModal = ({
    show,
    onHide,
    title,
    sectionKey = '',
    data = [],
    loading: parentLoading = false,
    fetchUrl // URL to fetch full data (e.g. /bet-list/casino/all-downline)
}) => {
    const [filters, setFilters] = useState({
        userSearch: '',
        ipAddress: '',
        fromAmount: '',
        toAmount: '',
        type: 'All'
    });
    const [internalData, setInternalData] = useState(data);
    const [filteredData, setFilteredData] = useState(data);
    const [loading, setLoading] = useState(parentLoading);

    // Sync internalData with data prop (initial or if prop changes)
    useEffect(() => {
        if (!fetchUrl) {
            setInternalData(data);
        }
    }, [data, fetchUrl]);

    // Fetch data if fetchUrl is provided and modal is shown
    useEffect(() => {
        const fetchData = async () => {
            if (!fetchUrl || !show) return;
            setLoading(true);
            try {
                // Correctly identify token based on active role
                const activeRole = localStorage.getItem('activeRole');
                let token = localStorage.getItem('token');
                if (isOwnerRole(activeRole)) {
                    token = localStorage.getItem('ownerToken');
                } else if (activeRole === 'staff') {
                    token = localStorage.getItem('staffToken');
                }

                const url = `${API_BASE_URL}${fetchUrl}`;
                // Append sectionKey as gameName if present
                const finalUrl = sectionKey ? `${url}?gameName=${sectionKey}` : url;

                const res = await axios.get(finalUrl, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data && res.data.success) {
                    setInternalData(res.data.data || []);
                }
            } catch (err) {
                console.error("Error fetching ViewMoreModal data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [fetchUrl, show, sectionKey]);

    // Update filteredData when internalData changes
    useEffect(() => {
        setFilteredData(internalData);
    }, [internalData]);

    // Reset filters when modal closes
    useEffect(() => {
        if (!show) {
            setFilters({
                userSearch: '',
                ipAddress: '',
                fromAmount: '',
                toAmount: '',
                type: 'All'
            });
            // Reset to current internalData (which might be prop data if fetchUrl unused)
            setFilteredData(internalData);
        }
    }, [show, internalData]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleSearch = () => {
        let filtered = [...internalData];

        if (filters.userSearch) {
            filtered = filtered.filter(bet =>
                (bet.username || bet.userName || '').toLowerCase().includes(filters.userSearch.toLowerCase())
            );
        }

        if (filters.ipAddress) {
            filtered = filtered.filter(bet =>
                (bet.ip || '').includes(filters.ipAddress)
            );
        }

        if (filters.fromAmount) {
            const fromAmount = parseFloat(filters.fromAmount);
            filtered = filtered.filter(bet => {
                const amount = parseFloat(bet.amount || bet.stake || 0);
                return amount >= fromAmount;
            });
        }

        if (filters.toAmount) {
            const toAmount = parseFloat(filters.toAmount);
            filtered = filtered.filter(bet => {
                const amount = parseFloat(bet.amount || bet.stake || 0);
                return amount <= toAmount;
            });
        }

        if (filters.type && filters.type !== 'All') {
            filtered = filtered.filter(bet =>
                (bet.type || bet.gametype || '').toLowerCase() === filters.type.toLowerCase()
            );
        }

        setFilteredData(filtered);
    };

    const handleReset = () => {
        setFilters({
            userSearch: '',
            ipAddress: '',
            fromAmount: '',
            toAmount: '',
            type: 'All'
        });
        setFilteredData(internalData);
    };

    // Enhanced columns for modal table
    const modalColumns = [
        { key: 'no', label: 'No', minWidth: '50px' },
        { key: 'username', label: 'Username', minWidth: '120px' },
        { key: 'marketName', label: 'Market Name', minWidth: '150px' },
        { key: 'nation', label: 'Nation', minWidth: '120px' },
        { key: 'type', label: 'Type', minWidth: '100px' },
        { key: 'userRate', label: 'User Rate', minWidth: '100px' },
        { key: 'amount', label: 'Amount', minWidth: '100px' },
        { key: 'currency', label: 'Currency', minWidth: '100px' },
        { key: 'placeDate', label: 'Place Date', minWidth: '120px' },
        { key: 'matchDate', label: 'Match Date', minWidth: '120px' },
        { key: 'ip', label: 'IP', minWidth: '120px' },
        { key: 'browserDetails', label: 'Browser Details', minWidth: '150px' },
    ];

    const renderModalCell = (bet, columnKey, index) => {
        switch (columnKey) {
            case 'no':
                return index + 1;
            case 'username':
                return bet.username || bet.userName || 'You';
            case 'marketName':
                return bet.marketName || bet.market || '-';
            case 'nation':
                return bet.matchedBet || bet.nation || '-';
            case 'type':
                return bet.type || bet.gametype || 'Back/Lay';
            case 'userRate':
                return bet.odds || bet.userrate || '-';
            case 'amount':
                return bet.stake || bet.amount || '-';
            case 'currency':
                return bet.currency || 'INR';
            case 'placeDate':
                return bet.placeDate || '-';
            case 'matchDate':
                return bet.matchDate || '-';
            case 'ip':
                return bet.ip || '-';
            case 'browserDetails':
                return bet.browserDetails || bet.browser || '-';
            default:
                return bet[columnKey] || '-';
        }
    };

    if (!show) return null;

    return (
        <div className={`modal show d-block ${styles.modalOverlay}`} tabIndex="-1" role="dialog">
            <div className={`modal-dialog ${styles.modalDialogCustom}`} role="document" style={{ maxWidth: '85%', width: '85%' }}>
                <div className={`modal-content ${styles.casinoResultModal}`} data-section-key={sectionKey}>
                    <header className={`modal-header ${styles.modalHeader}`}>
                        <h5 className={`modal-title ${styles.modalTitle}`}>
                            {title}
                        </h5>
                        <button
                            type="button"
                            className={`close ${styles.closeButton}`}
                            onClick={onHide}
                            aria-label="Close"
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </header>
                    <div className={`modal-body ${styles.modalBody}`}>
                        {loading ? (
                            <div className="text-center p-4">
                                <div className="spinner-border text-light" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className={modalStyles.modalContent}>
                                <div className={modalStyles.modalTitle}>Matched Bets</div>

                                <div style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", padding: "10px" }}>

                                    {/* Filter Section */}
                                    <div className={modalStyles.filterSection}>
                                        <div className={modalStyles.filterRow}>
                                            <div className={modalStyles.filterGroup}>
                                                <label className={modalStyles.filterLabel}>Search by user</label>
                                                <input
                                                    type="text"
                                                    value={filters.userSearch}
                                                    onChange={(e) => handleFilterChange('userSearch', e.target.value)}
                                                    placeholder="Enter Atleast 3 character"
                                                    className={modalStyles.filterInput}
                                                />
                                            </div>

                                            <div className={modalStyles.filterGroup}>
                                                <label className={modalStyles.filterLabel}>IP Address</label>
                                                <input
                                                    type="text"
                                                    value={filters.ipAddress}
                                                    onChange={(e) => handleFilterChange('ipAddress', e.target.value)}
                                                    placeholder="IP Address"
                                                    className={modalStyles.filterInput}
                                                />
                                            </div>

                                            <div className={modalStyles.filterGroup}>
                                                <label className={modalStyles.filterLabel}>Amount</label>
                                                <div className={modalStyles.amountGroup}>
                                                    <input
                                                        type="number"
                                                        value={filters.fromAmount}
                                                        onChange={(e) => handleFilterChange('fromAmount', e.target.value)}
                                                        placeholder="From Amount"
                                                        className={modalStyles.filterInput}
                                                    />
                                                    <span className={modalStyles.amountSeparator}>-</span>
                                                    <input
                                                        type="number"
                                                        value={filters.toAmount}
                                                        onChange={(e) => handleFilterChange('toAmount', e.target.value)}
                                                        placeholder="To Amount"
                                                        className={modalStyles.filterInput}
                                                    />
                                                </div>
                                            </div>

                                            <div style={{ flex: 1, width: '16%' }} className={modalStyles.filterGroup}>
                                                <label className={modalStyles.filterLabel}>Type</label>
                                                <select
                                                    value={filters.type}
                                                    onChange={(e) => handleFilterChange('type', e.target.value)}
                                                    className={modalStyles.filterSelect}
                                                >
                                                    <option value="All">All</option>
                                                    <option value="Back">Back</option>
                                                    <option value="Lay">Lay</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className={modalStyles.filterActions}>
                                            <button className={modalStyles.searchBtn} onClick={handleSearch}>
                                                <FontAwesomeIcon icon={faSearch} className={modalStyles.searchIcon} />
                                                Search
                                            </button>
                                            <button className={modalStyles.resetBtn} onClick={handleReset}>
                                                Reset
                                            </button>
                                        </div>
                                    </div>

                                    {/* Enhanced Table */}
                                    <div className="table-responsive" style={{ width: '100%', overflowX: 'auto', maxWidth: '100%' }}>
                                        <table className={`table m-b-0 ${modalStyles.couponTable}`} style={{ width: '100%', tableLayout: 'auto' }}>
                                            <thead>
                                                <tr className={modalStyles.tableHeader}>
                                                    {modalColumns.map((column, index) => (
                                                        <th
                                                            key={index}
                                                            style={{
                                                                color: "#fff",
                                                                minWidth: column.minWidth || undefined,
                                                                ...column.style,
                                                            }}
                                                        >
                                                            {column.label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody style={{ color: "#fff", backgroundColor: "transparent" }}>
                                                {filteredData.length > 0 ? (
                                                    filteredData.map((bet, idx) => {
                                                        const isLay = (bet.gametype && bet.gametype.toLowerCase().includes('lay')) || 
                                                                      (bet.modetype && bet.modetype.toLowerCase() === 'lay');
                                                        const rowClass = isLay ? modalStyles.layRow : modalStyles.backRow;

                                                        return (
                                                            <tr key={idx} className={rowClass}>
                                                                {modalColumns.map((column, colIndex) => (
                                                                    <td
                                                                        key={colIndex}
                                                                        style={{
                                                                            // color: "#fff", // Let row style dictate color
                                                                            minWidth: column.minWidth || undefined,
                                                                            ...column.cellStyle,
                                                                        }}
                                                                    >
                                                                        {renderModalCell(bet, column.key, idx)}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr style={{ color: "#fff", backgroundColor: "transparent" }}>
                                                        <td colSpan={modalColumns.length} className={modalStyles.noRecords}>
                                                            There are no records to show
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewMoreModal;
